import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import Reveal from '../components/common/Reveal';
import { getBreadcrumbSchema } from '../utils/seoSchemas';

// CimonaMía: real workshop photography already in the media library (used
// on the About page's "Qué hacemos" section) — hands hand-embroidering a
// reclaimed garment, exactly the redesign story this section tells.
const IMG_CIMONAMIA = '/media/imported/7aebf0043e4174f6f09bd2b00cabafe9f9662952.jpg';
// ReBorn: a curated stack of folded jeans — reads as inspection/curation.
const IMG_REBORN = 'https://images.unsplash.com/photo-1604176354204-9268737828e4?w=1200&q=80';

/**
 * Oversized decorative italic glyph — same device AboutPage.jsx uses behind
 * its sections.
 */
const BigGhostNumber = ({ children, top, right, left, bottom, size = '32vw', opacity = 0.06 }) => (
  <span
    aria-hidden="true"
    style={{
      position: 'absolute',
      top,
      right,
      left,
      bottom,
      fontFamily: 'var(--font-display)',
      fontStyle: 'italic',
      fontSize: size,
      lineHeight: 0.85,
      color: 'var(--terracotta)',
      opacity,
      pointerEvents: 'none',
      userSelect: 'none',
      fontVariationSettings: "'opsz' 144, 'SOFT' 100",
    }}
  >
    {children}
  </span>
);


const REBORN_CSS = `
  .rb-hero { position: relative; padding: 160px 40px 120px; overflow: hidden; min-height: 80vh; display: flex; align-items: center; }
  .rb-section { position: relative; padding: 100px 40px; }
  .rb-split { display: grid; gap: 80px; align-items: center; }
  .rb-split--hero { position: relative; width: 100%; grid-template-columns: 1.2fr 0.8fr; }
  .rb-split--left { grid-template-columns: 0.9fr 1.1fr; }
  .rb-split--right { grid-template-columns: 1.1fr 0.9fr; }
  .rb-photo { width: 100%; aspect-ratio: 4 / 5; object-fit: cover; display: block; }
  .rb-process { position: relative; padding: 120px 40px; text-align: center; }
  .rb-process-head { max-width: 720px; margin: 0 auto 80px; }
  .rb-steps { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 40px; text-align: left; }
  .rb-recognize { position: relative; padding: 100px 40px; text-align: center; background: var(--bg-alt, #f5f0e8); }
  .rb-quote { background: var(--bg-dark); color: #f5f0e8; padding: 160px 40px; text-align: center; }
  .rb-quote-mark { font-family: var(--font-display); font-style: italic; font-variation-settings: 'opsz' 144, 'SOFT' 100; font-size: 180px; line-height: 0.4; color: var(--terracotta); margin-bottom: 30px; }
  .rb-cta { padding: 100px 40px; text-align: center; }
  .rb-lead { max-width: 560px; }

  @media (max-width: 1023px) {
    .rb-split { gap: 48px; }
    .rb-section, .rb-recognize, .rb-cta { padding-left: 32px; padding-right: 32px; }
    .rb-process { padding: 96px 32px; }
    .rb-quote { padding: 120px 32px; }
  }
  @media (max-width: 860px) {
    .rb-hero { padding: 112px 24px 64px; min-height: auto; }
    .rb-split, .rb-split--hero, .rb-split--left, .rb-split--right { grid-template-columns: 1fr; gap: 32px; }
    .rb-hero-art { max-width: 320px; width: 100%; margin: 0 auto; }
    .rb-section { padding: 64px 24px; }
    .rb-lead { max-width: none; }
    .rb-photo { aspect-ratio: 4 / 3; }
    .rb-process { padding: 72px 24px; }
    .rb-process-head { margin-bottom: 40px; }
    .rb-steps { grid-template-columns: 1fr 1fr; gap: 32px 24px; }
    .rb-recognize { padding: 64px 24px; }
    .rb-quote { padding: 88px 24px; }
    .rb-quote-mark { font-size: 110px; margin-bottom: 18px; }
    .rb-cta { padding: 64px 24px; }
  }
  @media (max-width: 560px) {
    .rb-steps { grid-template-columns: 1fr; }
    .rb-hero { padding-top: 96px; }
  }
`;

const PROCESS_STEP_KEYS = [
  { n: '01', key: 'stepSelection' },
  { n: '02', key: 'stepReview' },
  { n: '03', key: 'stepCleaning' },
  { n: '04', key: 'stepApproval' },
];

const ReBornPage = () => {
  const { t } = useTranslation();
  const breadcrumbs = [
    { name: t('reborn.breadcrumbHome'), url: '/' },
    { name: 'ReBorn', url: '/reborn' },
  ];

  return (
    <>
      <SEOHead
        title="ReBorn"
        description={t('reborn.seoDescription')}
        url="/reborn"
        type="website"
      />
      <script type="application/ld+json">{JSON.stringify(getBreadcrumbSchema(breadcrumbs))}</script>

      <div style={{ background: 'var(--bg)', overflow: 'hidden' }} data-testid="reborn-page">
        <style>{REBORN_CSS}</style>
        {/* ═════════════ HERO ═════════════ */}
        <section data-testid="reborn-hero" className="rb-hero">
          <BigGhostNumber top="6vw" right="-3vw" size="46vw" opacity={0.05}>✦</BigGhostNumber>
          <div className="container mx-auto rb-split rb-split--hero">
            <Reveal stagger y={32}>
              <span
                className="eyebrow"
                style={{ color: 'var(--ink-soft)', display: 'inline-flex', alignItems: 'center', gap: '12px' }}
              >
                <span style={{ display: 'inline-block', width: '36px', height: '1px', background: 'var(--terracotta)' }} />
                {t('reborn.heroEyebrow')}
              </span>
              <h1
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(44px, 7vw, 96px)',
                  fontWeight: 300,
                  lineHeight: 0.98,
                  letterSpacing: '-2.5px',
                  marginTop: '28px',
                  marginBottom: '32px',
                  color: 'var(--ink)',
                }}
              >
                <Trans i18nKey="reborn.heroTitle" components={{ em: <em /> }} />
              </h1>
              <p style={{ fontSize: '17px', lineHeight: 1.75, color: 'var(--ink-soft)', maxWidth: '520px' }}>
                {t('reborn.heroText')}
              </p>
            </Reveal>

            <Reveal y={48} className="rb-hero-art">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  aspectRatio: '4 / 5',
                  background: 'var(--bg-dark)',
                }}
              >
                <img
                  src="/reborn-logo.png"
                  alt={t('reborn.sealAlt')}
                  style={{ width: '60%', height: 'auto', background: '#FEFCF7', padding: '6%', borderRadius: '24px' }}
                />
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═════════════ 01 — COLECCIÓN CIMONAMÍA ═════════════ */}
        <section data-testid="reborn-cimonamia" className="rb-section">
          <div className="container mx-auto rb-split rb-split--left">
            <Reveal y={32}>
              <img
                src={IMG_CIMONAMIA}
                alt={t('reborn.workshopAlt')}
                loading="lazy"
                decoding="async"
                className="rb-photo"
              />
            </Reveal>
            <Reveal stagger y={24}>
              <span className="eyebrow" style={{ display: 'block', marginBottom: '24px' }}>
                {t('reborn.cimonamiaEyebrow')}
              </span>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(32px, 4.2vw, 52px)',
                  fontWeight: 400,
                  lineHeight: 1.08,
                  letterSpacing: '-1px',
                  color: 'var(--ink)',
                  marginBottom: '28px',
                }}
              >
                <Trans i18nKey="reborn.cimonamiaTitle" components={{ em: <em /> }} />
              </h2>
              <div className="rb-lead" style={{ fontSize: '16px', lineHeight: 1.8, color: 'var(--ink-soft)' }}>
                <p style={{ marginBottom: '18px' }}>
                  {t('reborn.cimonamiaP1')}
                </p>
                <p style={{ marginBottom: '18px' }}>
                  {t('reborn.cimonamiaP2')}
                </p>
                <p
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontStyle: 'italic',
                    fontSize: '20px',
                    color: 'var(--ink)',
                    lineHeight: 1.5,
                    marginTop: '28px',
                  }}
                >
                  {t('reborn.cimonamiaP3')}
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═════════════ 02 — REBORN ═════════════ */}
        <section data-testid="reborn-reborn" className="rb-section" style={{ background: 'var(--bg-alt, #f5f0e8)' }}>
          <div className="container mx-auto rb-split rb-split--right">
            <Reveal stagger y={24} style={{ order: 1 }}>
              <span className="eyebrow" style={{ display: 'block', marginBottom: '24px' }}>
                {t('reborn.rebornEyebrow')}
              </span>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(32px, 4.2vw, 52px)',
                  fontWeight: 400,
                  lineHeight: 1.08,
                  letterSpacing: '-1px',
                  color: 'var(--ink)',
                  marginBottom: '28px',
                }}
              >
                <Trans i18nKey="reborn.rebornTitle" components={{ em: <em /> }} />
              </h2>
              <div className="rb-lead" style={{ fontSize: '16px', lineHeight: 1.8, color: 'var(--ink-soft)' }}>
                <p style={{ marginBottom: '18px' }}>
                  {t('reborn.rebornP1')}
                </p>
                <p style={{ marginBottom: '18px' }}>
                  {t('reborn.rebornP2')}
                </p>
                <p
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontStyle: 'italic',
                    fontSize: '20px',
                    color: 'var(--ink)',
                    lineHeight: 1.5,
                    marginTop: '28px',
                  }}
                >
                  {t('reborn.rebornP3')}
                </p>
              </div>
            </Reveal>
            <Reveal y={32} style={{ order: 2 }}>
              <img
                src={IMG_REBORN}
                alt={t('reborn.curationAlt')}
                loading="lazy"
                decoding="async"
                className="rb-photo"
              />
            </Reveal>
          </div>
        </section>

        {/* ═════════════ EL PROCESO REBORN ═════════════ */}
        <section data-testid="reborn-process" className="rb-process">
          <Reveal className="rb-process-head">
            <span className="eyebrow" style={{ display: 'block', marginBottom: '24px' }}>
              {t('reborn.processEyebrow')}
            </span>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(30px, 3.6vw, 44px)',
                fontWeight: 400,
                lineHeight: 1.15,
                letterSpacing: '-1px',
                color: 'var(--ink)',
              }}
            >
              <Trans i18nKey="reborn.processTitle" components={{ em: <em /> }} />
            </h2>
          </Reveal>

          <Reveal stagger className="container mx-auto rb-steps">
            {PROCESS_STEP_KEYS.map((step) => (
              <div key={step.n}>
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontStyle: 'italic',
                    fontSize: '15px',
                    color: 'var(--terracotta)',
                    display: 'block',
                    marginBottom: '12px',
                  }}
                >
                  {step.n}
                </span>
                <h3
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '22px',
                    fontWeight: 500,
                    color: 'var(--ink)',
                    marginBottom: '10px',
                  }}
                >
                  {t(`reborn.${step.key}Title`)}
                </h3>
                <p style={{ fontSize: '14.5px', lineHeight: 1.7, color: 'var(--ink-soft)' }}>{t(`reborn.${step.key}Body`)}</p>
              </div>
            ))}
          </Reveal>
        </section>

        {/* ═════════════ CÓMO RECONOCERLAS ═════════════ */}
        <section data-testid="reborn-recognize" className="rb-recognize">
          <Reveal stagger style={{ maxWidth: '560px', margin: '0 auto' }}>
            <img
              src="/reborn-logo.png"
              alt={t('reborn.sealAlt')}
              style={{ width: '160px', height: 'auto', margin: '0 auto 32px' }}
            />
            <span className="eyebrow" style={{ display: 'block', marginBottom: '20px' }}>
              {t('reborn.recognizeEyebrow')}
            </span>
            <p
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(22px, 2.6vw, 30px)',
                fontWeight: 400,
                lineHeight: 1.4,
                color: 'var(--ink)',
              }}
            >
              {t('reborn.recognizeText')}
            </p>
          </Reveal>
        </section>

        {/* ═════════════ QUOTE DE CIERRE ═════════════ */}
        <section data-testid="reborn-quote" className="rb-quote">
          <Reveal stagger y={24}>
            <div aria-hidden="true" className="rb-quote-mark">
              “
            </div>
            <blockquote
              style={{
                fontFamily: 'var(--font-display)',
                fontVariationSettings: "'opsz' 144, 'SOFT' 50",
                fontSize: 'clamp(28px, 4.2vw, 56px)',
                fontWeight: 300,
                lineHeight: 1.15,
                letterSpacing: '-1px',
                maxWidth: '820px',
                margin: '0 auto 40px',
              }}
            >
              {t('reborn.quote')}
            </blockquote>
            <div aria-hidden="true" className="rb-quote-mark">
              ”
            </div>
            <cite
              style={{
                fontStyle: 'normal',
                fontFamily: 'var(--font-body)',
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '3px',
                color: 'var(--terracotta)',
              }}
            >
              {t('reborn.quoteAuthor')}
            </cite>
          </Reveal>
        </section>

        {/* ═════════════ CTA ═════════════ */}
        <section className="rb-cta">
          <Reveal stagger>
            <p style={{ fontSize: '16px', color: 'var(--ink-soft)', marginBottom: '28px' }}>
              {t('reborn.ctaText')}
            </p>
            <Link to="/shop" className="btn-primary" data-testid="reborn-cta-btn">
              {t('reborn.ctaButton')}
            </Link>
          </Reveal>
        </section>
      </div>
    </>
  );
};

export default ReBornPage;
